import React, { useEffect, useMemo, useState } from 'react';
import Polygon from '@arcgis/core/geometry/Polygon';
import * as multiPartToSinglePartOperator from '@arcgis/core/geometry/operators/multiPartToSinglePartOperator';
import { InfoIcon, PlusIcon, XIcon } from '@phosphor-icons/react';
import { Chip, Loader, EmptyState, SearchInput } from '@origo/origo-ui';
import { Toaster } from '@origo/origo-components';
import {
    NaboListeService,
    MatrikkelEnhet,
    type AvvikPort,
    type EiendomsvelgerState,
} from '@origo/matrikkel-services';
import { createArcgisAvvikRepository, anvendDeltaerPåEnhetstre } from '@origo/seksjon';
import { useAppSelector, selectGdoToken } from '../../store/storeHooks';
import { useMapViewContext } from '@origo/map-context';
import { createLogger } from '@origo/logger';
import type { EiendomsvelgerTarget } from './eiendomsvelgerTarget';
import {
    eiendomKey,
    filterGateGroups,
    getEiendomDisplayLabel,
    groupAndSortEiendommer,
    type GateEntry,
} from './eiendommerGruppering';
import styles from './Eiendommer.module.css';

const log = createLogger('Eiendommer');

/** Skeleton-lastetilstand: antall chip-plassholdere pr. gate-rad. */
const SKELETON_ROWS = [8, 6, 7, 5, 4];

const TOM_TILSTAND: EiendomsvelgerState = {
    eiendommer: [],
    fjernedeEnheter: [],
    fjernedeAdresser: [],
    saving: false,
};

/**
 * Del et flerdelt polygon (f.eks. en plan med flere separate planområder) i
 * enkeltdeler. Eiendomsoppslaget og overlapp-filteret skal kjøre per del:
 * overlapp-andelen regnes mot det minste av områdets og teigens areal, så en
 * stor teig som klipper ett planområde kunne ellers falt under terskelen når
 * området ble målt som hele unionen (#12806). Annet enn polygon returneres urørt.
 */
function geometryParts(geometry: unknown): unknown[] {
    const g = geometry as __esri.Geometry | __esri.PolygonProperties;
    const polygon = 'type' in g && g.type ? g : Polygon.fromJSON(g);
    if (polygon.type !== 'polygon') return [geometry];
    const parts = multiPartToSinglePartOperator.executeMany([polygon as Polygon]);
    return parts.length > 0 ? parts : [geometry];
}

/** Endrings-oppsummering for økten — antall valgte nå, samt hvor mange som er
 *  lagt til / fjernet siden grunnsettet ble lastet. Drives oppover så panelet
 *  kan vise «X lagt til, Y fjernet» i footeren. */
export interface EiendommerSummary {
    total: number;
    added: number;
    removed: number;
    /** Sant mens en delta-skriving pågår, så footeren kan vise «Lagrer…». */
    saving: boolean;
}

interface EiendommerProps {
    /** Hierarkinivået eiendommene knyttes til. Se buildEiendomsvelgerTarget. */
    target: EiendomsvelgerTarget;
    eiendommerUrl?: string;
    /** Eldre flat-visning (egendefinert inline-seksjon). Default er gruppert. */
    flat?: boolean;
    showEmptyState?: boolean;
    /** Rapporterer status oppover: null mens lasten pågår, ellers oppsummering. */
    onSummaryChange?: (summary: EiendommerSummary | null) => void;
}

type NaboListeServiceWithClear = NaboListeService & {
    clearSelection?: () => Promise<void>;
};

const Eiendommer: React.FC<EiendommerProps> = ({ target, eiendommerUrl, flat = false, showEmptyState = false, onSummaryChange }) => {
    const { mapView } = useMapViewContext();
    const naboServiceRef = React.useRef<NaboListeService | null>(null);
    // Hele tjenestens tilstand i ett objekt — tjenesten emitterer den som ett
    // øyeblikksbilde, så UI-et aldri ser en halv oppdatering. Fjernede enheter og
    // adresser vises nedtonet på sin plass i gate-gruppa med «+», så de kan legges
    // tilbake — også etter relasting.
    const [state, setState] = useState<EiendomsvelgerState>(TOM_TILSTAND);
    const { eiendommer, fjernedeEnheter, fjernedeAdresser, saving } = state;
    const [loading, setLoading] = useState(false);
    const [search, setSearch] = useState('');
    // Angre-toast: matrikkelenhetene som nettopp ble fjernet, så de kan legges
    // tilbake hvis saksbehandleren angrer.
    const [undoToast, setUndoToast] = useState<{
        eiendommer?: MatrikkelEnhet[];
        /** Satt når det var en enkeltadresse som ble fjernet (13928). */
        adresseIds?: number[];
        label: string;
    } | null>(null);
    // Grunnsettet (auto-inkluderte) snapshottes ved første ferdig-lasting, så vi
    // kan vise hvor mange saksbehandleren har lagt til i økten.
    const baselineRef = React.useRef<Set<string> | null>(null);
    const token = useAppSelector(selectGdoToken) || '';
    const apiUrl = 'https://geoeiendom.no/api/';

    // Bygg det løse «feature»-objektet NaboListeService forventer, med stabil
    // identitet så effekten ikke kjører på nytt (og lager ny tjeneste-instans)
    // ved hver render. NaboListeService leser kun .geometry + .attributes.
    //
    // Nøkkelen er (omradeId, omradetype) — de identifiserer entydig det valgte
    // nivået. Vi kan IKKE ha target.geometry som dep: for delområdegruppe er
    // geometrien en union som lages på nytt ved hver recompute, og en ny
    // geometri-referanse ville rekonstruere tjenesten midt i en økt (→ activeOmradeId
    // transient undefined → delta-rader uten omradeid). Geometrien leses ved bygging;
    // den endrer seg kun logisk når omradeId gjør det.
    const feature = useMemo(() => ({
        geometry: target.geometry ?? null,
        attributes: { omradeid: target.omradeId, omradetype: target.omradetype },
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }), [target.omradeId, target.omradetype]);

    useEffect(() => {
        if (!mapView || !feature) return;

        // `cancelled` hindrer at en utgående instans (hvis effekten kjører på
        // nytt) skriver state. `loading` styres av at SELVE lasten resolver —
        // ikke av et enkelt event — så en stale `clearSelection([])`-dispatch
        // fra forrige instans ikke flasher empty state før dataene kommer.
        let cancelled = false;
        setLoading(true);
        setState(TOM_TILSTAND);
        baselineRef.current = null;
        // Avviksdomenet (drømmeplan-spesifikt) injiseres i den generiske
        // nabolisten (etappe 2A, B3): reglene + persisteringen bor i
        // @origo/seksjon; appen — som avhenger av begge pakkene — eier
        // koblingen. matrikkel-services forblir uten seksjon-avhengighet.
        const avvikPort: AvvikPort | undefined = eiendommerUrl
            ? {
                  ...createArcgisAvvikRepository({
                      eiendommerUrl,
                      ønsketWkid: () => mapView.spatialReference?.wkid,
                  }),
                  anvendPåEnhetstre: anvendDeltaerPåEnhetstre,
              }
            : undefined;
        const svc = new NaboListeService(mapView, token, apiUrl, eiendommerUrl ?? '', avvikPort);
        naboServiceRef.current = svc;

        // Direkte callback i stedet for globalt window-event: siden `svc` og
        // callbacken tilhører samme effekt-kjøring, kan ikke lista desynce ved
        // rekonstruksjon (klikk-handler og mottaker er alltid samme generasjon).
        // Ett øyeblikksbilde per emisjon — aldri felt-for-felt.
        svc.setOnChange((neste) => {
            if (cancelled) return;
            setState(neste);
        });

        log.debug('Henter eiendommer for feature:', feature);
        (async () => {
            try {
                // NaboListeService leser kun .geometry + .attributes fra «graphic»;
                // vi sender lette objekter, ikke ekte esri Graphics. Én per
                // polygon-del, så hvert planområde slås opp og overlapp-filtreres
                // for seg (#12806).
                const loadPromise = feature.geometry
                    ? svc.getIntersectingProperties(
                        geometryParts(feature.geometry).map((geometry) => ({ ...feature, geometry })) as never,
                        token,
                        apiUrl,
                      )
                    : feature.attributes?.omradeid != null
                        ? svc.loadPersistedProperties(
                            feature.attributes.omradeid,
                            feature.attributes?.omradetype ?? 'egendefinert',
                          )
                        : Promise.resolve();
                svc.activatePropertySelector();
                await loadPromise;
            } catch (err) {
                log.error('Kunne ikke laste eiendommer:', err);
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();

        return () => {
            cancelled = true;
            svc.setOnChange(undefined);
            svc.deactivatePropertySelector();
            void (svc as NaboListeServiceWithClear).clearSelection?.();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mapView, feature]);

    // Snapshot grunnsettet første gang lista er på plass, og rapporter delta.
    // Mens lasten pågår: rapporter null så footeren viser «Laster inn…».
    useEffect(() => {
        if (loading) {
            onSummaryChange?.(null);
            return;
        }
        const keys = eiendommer.map(eiendomKey);
        if (baselineRef.current === null) {
            baselineRef.current = new Set(keys);
        }
        if (!onSummaryChange) return;
        const baseline = baselineRef.current;
        const now = new Set(keys);
        let added = 0;
        now.forEach((k) => { if (!baseline.has(k)) added++; });
        // `removed` tas fra det persisterte fjernet-settet, ikke fra en diff mot
        // baselineRef: den snapshottes ETTER at deltaene er anvendt, så
        // fjerninger gjort i tidligere økter ville ikke blitt talt.
        // Begge spor summeres: fjernede enheter (13927) og fjernede adresser (13928).
        const removed = fjernedeEnheter.length + fjernedeAdresser.length;
        onSummaryChange({ total: eiendommer.length, added, removed, saving });
    }, [eiendommer, fjernedeEnheter, fjernedeAdresser, loading, saving, onSummaryChange]);

    // Cmd/Ctrl+Z angrer siste fjerning mens angre-toasten er aktiv.
    useEffect(() => {
        if (!undoToast) return;
        const onKey = (e: KeyboardEvent) => {
            if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'z') {
                e.preventDefault();
                void handleUndoRemove();
            }
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [undoToast]);

    const grouped = useMemo(
        () => groupAndSortEiendommer(eiendommer, fjernedeEnheter, fjernedeAdresser),
        [eiendommer, fjernedeEnheter, fjernedeAdresser],
    );
    const filtered = useMemo(() => filterGateGroups(grouped, search), [grouped, search]);

    function handleAdresseClick(gate: string, nr: string) {
        const valgtEiendom = eiendommer.find(e => {
            const { gate: eGate, label: eLabel } = getEiendomDisplayLabel(e);
            return eGate === gate && eLabel === nr;
        });
        if (valgtEiendom) {
            naboServiceRef.current?.gotoEiendom(valgtEiendom);
        }
    }

    /**
     * «Gå til i kart» for en chip. Tre tilfeller:
     *  - enkeltadresse (13928) → zoom til adressens egen koordinat
     *  - fjernet enhet (13927) → teigIds er tom, så baseline-teigene brukes
     *  - inkludert enhet → dens egne teiger
     */
    function handleEntryClick(gate: string, entry: GateEntry) {
        const svc = naboServiceRef.current;
        if (!svc) return;
        if (entry.adresse) {
            svc.gotoAdresse(entry.adresse);
            return;
        }
        if (entry.fjernet) {
            const eiendom = entry.eiendommer[0];
            if (eiendom) svc.gotoEiendom({ ...eiendom, teigIds: entry.baselineTeigIds ?? [] });
            return;
        }
        handleAdresseClick(gate, entry.label);
    }

    /** Fjern en chip — adresse-sporet når chippen er én adresse, ellers enhets-sporet. */
    async function handleRemoveEntry(gate: string, entry: GateEntry) {
        const svc = naboServiceRef.current;
        if (!svc) return;
        if (entry.adresse) {
            await svc.fjernAdresser([entry.adresse.adresseId]);
            setUndoToast({ adresseIds: [entry.adresse.adresseId], label: `${gate} ${entry.label}` });
            return;
        }
        await svc.removeMatrikkelEnheter(entry.eiendommer);
        setUndoToast({
            eiendommer: entry.eiendommer,
            label: `${gate} ${entry.label}`,
        });
    }

    /** Legg tilbake — én kodevei for både «+»-knappen og angre-toasten. */
    async function handleGjenopprett(entry: { eiendommer?: MatrikkelEnhet[]; adresseIds?: number[] }) {
        const svc = naboServiceRef.current;
        if (!svc) return;
        if (entry.adresseIds?.length) {
            await svc.gjenopprettAdresser(entry.adresseIds);
            return;
        }
        await svc.gjenopprettEnheter((entry.eiendommer ?? []).map((e) => e.matrikkelenhetId));
    }

    async function handleUndoRemove() {
        if (!undoToast) return;
        await handleGjenopprett(undoToast);
        setUndoToast(null);
    }

    // ── Eldre flat-visning (egendefinert inline-seksjon) ──
    // Egendefinerte områder bygger utvalget kun fra add-rader; en fjerning sletter
    // selve raden og etterlater ingen baseline. `fjernede` er derfor alltid tom her.
    if (flat) {
        return (
            <div className={styles.root}>
                <p className={styles.help}>
                    Klikk på en teig i kartet for å legge til eller fjerne. Hold nede shift for å legge til/fjerne alle teiger innen en matrikkelenhet.
                </p>
                {loading && (
                    <div className={styles.loading}>
                        <Loader size="sm" />
                        <span>Laster eiendommer…</span>
                    </div>
                )}
                {!loading && grouped.length === 0 && showEmptyState && (
                    <EmptyState
                        icon={<InfoIcon size={24} aria-hidden="true" />}
                        title="Ingen eiendommer lagt til"
                    />
                )}
                {!loading && grouped.length > 0 && (
                    <div className={styles.list}>
                        {grouped.flatMap(({ gate, entries }) =>
                            entries.map((entry) => (
                                <Chip
                                    key={`${gate}-${entry.label}`}
                                    className={styles.chip}
                                    onClick={() => handleEntryClick(gate, entry)}
                                    onRemove={() => void handleRemoveEntry(gate, entry)}
                                    removeLabel={`Fjern ${gate} ${entry.label}`}
                                >
                                    {gate} {entry.label}
                                </Chip>
                            ))
                        )}
                    </div>
                )}
            </div>
        );
    }

    // ── Gruppert visning (delt map-first eiendomsvelger) ──
    return (
        <div className={styles.root}>
            <SearchInput
                label="Søk opp i listen"
                value={search}
                onChange={setSearch}
                placeholder="Søk…"
            />

            {loading && (
                <div className={styles.skeleton} role="status" aria-label="Laster eiendommer">
                    {SKELETON_ROWS.map((count, i) => (
                        <div key={i} className={styles.gateGroup} aria-hidden="true">
                            <div className={styles.skeletonGate} />
                            <div className={styles.entries}>
                                {Array.from({ length: count }).map((_, j) => (
                                    <div key={j} className={styles.skeletonChip} />
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {!loading && grouped.length === 0 && showEmptyState && (
                <EmptyState
                    icon={<InfoIcon size={32} aria-hidden="true" />}
                    title="Ingen eiendommer i planområdet ennå"
                    description="Klikk en teig i kartet for å legge til eiendommer manuelt."
                />
            )}

            {!loading && grouped.length > 0 && filtered.length === 0 && (
                <EmptyState
                    icon={<InfoIcon size={32} aria-hidden="true" />}
                    title={`Ingen treff på «${search.trim()}»`}
                    description="Prøv et annet gatenavn eller nummer."
                />
            )}

            {!loading && filtered.length > 0 && (
                <div className={styles.groupedList}>
                    {filtered.map(({ gate, entries, antallInkludert, antallFjernet }) => (
                        <div key={gate} className={styles.gateGroup}>
                            <div className={styles.gateHeader}>
                                <span className={styles.gateLabel}>{gate}</span>
                                <span className={styles.countBadge}>{antallInkludert}</span>
                                {antallFjernet > 0 && (
                                    <span className={styles.removedCount}>
                                        {antallFjernet} fjernet
                                    </span>
                                )}
                            </div>
                            <div className={styles.entries}>
                                {entries.map((entry) => {
                                    const { label, fjernet, adresse } = entry;
                                    // Adresse-chips nøkles på adresseId; enhets-chips på etikett.
                                    const key = `${fjernet ? 'F' : 'I'}-${adresse?.adresseId ?? label}`;
                                    if (fjernet) {
                                        return (
                                            <span
                                                key={key}
                                                className={`${styles.numberChip} ${styles.removedChip}`}
                                            >
                                                <button
                                                    type="button"
                                                    className={styles.numberBtn}
                                                    onClick={() => handleEntryClick(gate, entry)}
                                                >
                                                    {label}
                                                </button>
                                                <button
                                                    type="button"
                                                    className={styles.addBtn}
                                                    aria-label={`Legg tilbake ${gate} ${label}`}
                                                    onClick={() => void handleGjenopprett(
                                                        adresse
                                                            ? { adresseIds: [adresse.adresseId] }
                                                            : { eiendommer: entry.eiendommer },
                                                    )}
                                                >
                                                    <PlusIcon size={14} weight="bold" aria-hidden="true" />
                                                </button>
                                            </span>
                                        );
                                    }
                                    return (
                                        <span key={key} className={styles.numberChip}>
                                            <button
                                                type="button"
                                                className={styles.numberBtn}
                                                onClick={() => handleEntryClick(gate, entry)}
                                            >
                                                {label}
                                            </button>
                                            <button
                                                type="button"
                                                className={styles.removeBtn}
                                                aria-label={`Fjern ${gate} ${label}`}
                                                onClick={() => void handleRemoveEntry(gate, entry)}
                                            >
                                                <XIcon size={14} weight="bold" aria-hidden="true" />
                                            </button>
                                        </span>
                                    );
                                })}
                            </div>
                        </div>
                    ))}
                </div>
            )}

            <Toaster
                open={undoToast !== null}
                variant="info"
                autoCloseDuration={6000}
                onClose={() => setUndoToast(null)}
                message={
                    undoToast && (
                        <span className={styles.undoToast}>
                            {undoToast.label} fjernet
                            <button
                                type="button"
                                className={styles.undoButton}
                                onClick={() => void handleUndoRemove()}
                            >
                                Angre
                            </button>
                        </span>
                    )
                }
            />
        </div>
    );
};

export default Eiendommer;
